
import {map} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { filter, each } from 'lodash';
import { OrganigrammeEntityLink } from './model/entity-link';
import { COMPANY_LINK, SHAREHOLDER_LINK } from './organigramme.settings';
import { OrganigrammeEntity, OrganigrammeEntityType } from './model/entity';
import { sortByNumberReverse, sortAlphabetically } from '../utils';

const DossierQuery = require('./gql/dossier.query.gql');
const RecordQuery = require('./gql/record.query.gql');
const LinksQuery = require('./gql/links.query.gql');

const sortByGroupOrderFn = sortByNumberReverse('order');
const sortByEntityNameFn = sortAlphabetically('entity.name');


export class OrganigrammeEntityLinkResult {
  belongs_to: OrganigrammeEntityLink[] = []
  has_shares_in: OrganigrammeEntityLink[] = []
}


/**
 * Service to get Organigramme data
 */
@Injectable()
export class OrganigrammeService {

  constructor(
    private apollo: Apollo,
    private http: HttpClient
  ) { }

  entity(type: OrganigrammeEntityType, id: string): Observable<OrganigrammeEntity> {
    if(type === 'dossier') {
      return this.dossier(id);
    }
    else {
      return this.record(id);
    }
  }


  dossier(id: string): Observable<OrganigrammeEntity> {
    return this.apollo
      .query({
        query: DossierQuery,
        variables: {
          id,
          link: COMPANY_LINK
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => {
        const data = result.data['dossier'];
        
        return OrganigrammeEntity.buildFromDossier(data);
      }));
  }


  record(id: string): Observable<OrganigrammeEntity> {
    return this.apollo
      .query({
        query: RecordQuery,
        variables: {
          id,
          link: COMPANY_LINK
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => {
        const data = result.data['record'];
        
        return OrganigrammeEntity.buildFromRecord(data);
      }));
  }


  links(dossier_id: string, record_id: string): Observable<OrganigrammeEntityLinkResult> {
    return this.apollo
      .query({
        query: LinksQuery,
        variables: {
          company_link: COMPANY_LINK,
          shareholder_link: SHAREHOLDER_LINK,
          has_dossier_id: dossier_id !== undefined,
          dossier_id: dossier_id || '',
          has_record_id: record_id !== undefined,
          record_id: record_id || ''
        },
        fetchPolicy: 'no-cache',
      }).pipe(
      map(result => {
        let links = new OrganigrammeEntityLinkResult();

        // Belongs to
        let belongsToData = result.data['belongs_to'];

        if(belongsToData) {
          this.processBelongsTo(belongsToData, links.belongs_to);
        }

        // Has shares in
        let hasSharesInData = result.data['has_shares_in'];

        if(hasSharesInData) {
          this.processHasSharesIn(hasSharesInData, links.has_shares_in);
        }

        return links;
      }));
  }

  private getLastGroupIdForDossier(dossier: any): string {
    // Get last group for shareholder links
    let groups = filter(dossier.link_groups, (lg: any) => lg.link_name === SHAREHOLDER_LINK && lg.link_type === 'RecordLink');

    groups.sort(sortByGroupOrderFn);

    let lastGroupId: string = groups.length > 0 ? groups[0].id : null;

    return lastGroupId;
  }

  private processBelongsTo(data: any, result: OrganigrammeEntityLink[]) {

    // Get last group for shareholder links
    let lastGroupId = this.getLastGroupIdForDossier(data);

    each(data.links, l => {
      // Ignore links that dosn't belong to the last group
      if (lastGroupId !== null && l.group_id !== lastGroupId) {
        return;
      }

      let link = new OrganigrammeEntityLink();

      link.entity = OrganigrammeEntity.buildFromRecord(l.record);

      link.parseShares(l);

      result.push(link);
    });

    result.sort(sortByEntityNameFn);
  }

  private processHasSharesIn(data: any, result: OrganigrammeEntityLink[]) {
    each(data.links, l => {
      // Get last group for shareholder links
      let lastGroupId = this.getLastGroupIdForDossier(l.dossier);

      // Ignore links that dosn't belong to the last group
      if (lastGroupId !== null && l.group_id !== lastGroupId) {
        return;
      }

      let link = new OrganigrammeEntityLink();

      link.entity = OrganigrammeEntity.buildFromDossier(l.dossier);

      link.parseShares(l);

      result.push(link);
    });

    result.sort(sortByEntityNameFn);
  }

  private downloadDocument(data: any, type: 'powerpoint' | 'excel') {
    this.http.post(`/files/organigramme/${type}`, data, {
      responseType: 'blob',
      observe: 'response'
    })
    .subscribe((res) => {
      const filename = res.headers.get('content-disposition').split('attachment; filename=')[1];
      const url = window.URL.createObjectURL(res.body);

      // Workaround to download a document
      const $a = document.createElement('a');
      $a.href = url;
      $a.download = filename;

      setTimeout(() => {
        $a.click();
      });
    }, (error: HttpErrorResponse) => {
      console.error(`${error.status} - ${error.statusText}`);
    });
  }

  generatePowerPoint(data: any) {
    this.downloadDocument(data, 'powerpoint');
  }

  generateExcel(data: any) {
    this.downloadDocument(data, 'excel');
  }
}